import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useRequestPasswordResetMutation } from "generated/graphql";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useSnack } from "hooks/useSnack";
import { validateResetEmail } from "../../validation";

interface RequestCodeStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSent: () => void;
}

/** Asks for the account's email and sends it a reset code. */
const RequestCodeStep: React.FC<RequestCodeStepProps> = ({
  email,
  onEmailChange,
  onSent,
}) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const [, requestReset] = useRequestPasswordResetMutation();
  const [sending, setSending] = useState(false);
  const { errors, validateAll, revalidate } =
    useFieldErrors(validateResetEmail);

  const handleSend = async () => {
    if (!validateAll({ email })) {
      return;
    }
    setSending(true);
    const response = await requestReset({ email: email.trim() });
    setSending(false);

    const error =
      response.data?.requestPasswordReset.errors?.[0]?.message ??
      response.error?.message;
    if (error) {
      showSnack(error, "error");
      return;
    }
    showSnack(
      `If ${email.trim()} has an account, we've sent it a code.`,
      "success",
    );
    onSent();
  };

  return (
    <>
      <Typography variant="body2" align="center">
        {t("forgot.intro")}
      </Typography>
      <TextField
        size="tiny"
        name="email"
        type="email"
        value={email}
        placeHolder={t("forgot.email_placeholder")}
        onChange={({ target: { value } }) => {
          onEmailChange(value);
          revalidate({ email: value });
        }}
        error={errors.email ?? undefined}
      />
      <Button
        onClick={handleSend}
        variant="contained"
        color="warning"
        size="small"
        style={{ color: theme.palette.common.white }}
        loading={sending}
        disabled={sending}
        fullWidth
      >
        {t("forgot.send_code")}
      </Button>
    </>
  );
};

export default RequestCodeStep;
